import { ChangeEvent, FC, useEffect, useState } from "react";

import { Alert, Button, Stack, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  GeoDataFileModel,
  listGeoDataFile,
  postGeoDataFile,
  postGeoDataFileComplete,
} from "@rubi/api-generated";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";

import { DevPage, ErrorMsg, Section } from "./common";
import {
  onRowActivateKeyDown,
  RegionBreadcrumbs,
  RegionSubNav,
} from "./regionShared";

const columns: GridColDef<GeoDataFileModel>[] = [
  { field: "filename", headerName: "Filename", flex: 1, minWidth: 220 },
  { field: "status", headerName: "Status", width: 120 },
  {
    field: "size_bytes",
    headerName: "Size",
    width: 120,
    valueFormatter: (value: number) => value.toLocaleString(),
  },
  {
    field: "time_created",
    headerName: "Time Created",
    width: 200,
    valueFormatter: (value: string) => new Date(value).toLocaleString(),
  },
];

export const DevGeoDataFileListPage: FC = () => {
  const { orgId = "" } = useParams();
  const navigate = useNavigate();

  const [files, setFiles] = useState<GeoDataFileModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const loadFiles = async () => {
    setLoading(true);
    const response = await listGeoDataFile({
      path: { org_id: orgId },
      throwOnError: false,
    });
    if (response.status === 200) {
      setFiles(response.data?.geo_data_files ?? []);
      setError(null);
    } else {
      setError(`${response.status} ${response.error?.console_msg ?? ""}`);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadFiles();
  }, [orgId]);

  const openFile = (fileId: string) =>
    navigate(`/dev_admin/region/${orgId}/geo_data_file/${fileId}`);

  const handleUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setUploading(true);
    setUploadError(null);
    try {
      const { data } = await postGeoDataFile({
        path: { org_id: orgId },
        body: {
          filename: file.name,
          content_type: file.type || "application/octet-stream",
          size_bytes: file.size,
        },
      });
      if (!data) throw new Error("No upload details returned");

      const headers: { [key: string]: string } = {};
      data.upload.headers.forEach((h) => {
        headers[h.key] = h.value;
      });
      await axios.put(data.upload.uri, file, { headers });

      await postGeoDataFileComplete({
        path: { org_id: orgId, file_id: data.geo_data_file.id },
      });

      await loadFiles();
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <DevPage
      title="GeoDataFiles"
      breadcrumbs={<RegionBreadcrumbs orgId={orgId} />}
    >
      <RegionSubNav orgId={orgId} active="geo_data_file" />

      <Section subtitle="Upload a new file">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          A GeoJSON file, or a zip of ArcGIS data. The original file is kept
          only in S3 — processing it below extracts Subregions from it.
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button variant="contained" component="label" disabled={uploading}>
            {uploading ? "Uploading…" : "Choose file"}
            <input type="file" hidden onChange={handleUpload} />
          </Button>
        </Stack>
        {uploadError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {uploadError}
          </Alert>
        )}
      </Section>

      <Section subtitle="Existing files">
        <ErrorMsg msg={error} />
        <div style={{ width: "100%" }}>
          <DataGrid
            rows={files}
            columns={columns}
            loading={loading}
            autoHeight
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            pageSizeOptions={[10, 25, 50]}
            onRowClick={(params) => openFile(String(params.id))}
            onCellKeyDown={onRowActivateKeyDown(openFile)}
            sx={{ cursor: "pointer" }}
          />
        </div>
      </Section>
    </DevPage>
  );
};
